import { Button, Dialog, DialogTitle, Grid, IconButton, LinearProgress, Link, Tooltip } from '@material-ui/core';
import { DatePicker, Input } from '../../Inputs';
import React, { FC, useState } from 'react';
import {
  Scalars,
  SportingEventDocument,
  UpdateSportingEventMutationHookResult,
  UploadFileMutationHookResult,
  useSportingEventQuery,
  useUpdateSportingEventMutation,
  useUploadFileMutation,
} from '../../../api';

import DeleteIcon from '@material-ui/icons/Delete';
import DocumentSelect from '../../../module/Documents';
import DocumentTypes from '../../../module/DocumentTypes';
import { DropzoneArea } from 'material-ui-dropzone';
import EditLocationIcon from '@material-ui/icons/EditLocation';
import SaveIcon from '@material-ui/icons/Save';
import { detectedChanges } from '../../../utils/compareSportingEvents';
import getMessage from '../../../messages';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';

const useStyles = makeStyles({
  title: {
    fontWeight: 500,
  },
  link: {
    cursor: 'pointer',
  },
  btn: {
    padding: '5px 5px',
    minWidth: '15px',
  },
  icon: {
    fontSize: 20,
  },
  verticalSpaceMarge: {
    marginBottom: 35,
    padding: 10,
  },
  dropZone: {
    minHeight: 30,
    border: 0,
    borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
    borderRadius: 0,

    '& > .MuiDropzonePreviewList-root': {
      margin: '0 !important',
      width: '100%',
      '& > .MuiDropzonePreviewList-imageContainer': {
        flexBasis: 'unset',
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 15,
        '& > svg': {
          height: '40px',
        },
        '& > p': {
          marginLeft: 15,
          marginRight: 15,
        },
        '& > button': {
          top: 'unset',
          right: 0,
          position: 'unset',
          marginLeft: 30,

          color: '#d32f2f',
          boxShadow: 'unset',
        },
      },
    },
    '& > .MuiDropzoneArea-textContainer': {
      display: 'flex',

      '& > .MuiDropzoneArea-text': {
        margin: 0,
        fontSize: 15,
        '&:after': {
          content: '"*"',
          color: 'red',
          verticalAlign: 'middle',
          marginLeft: 4,
        },
      },
      '& > .MuiDropzoneArea-icon': {
        width: 20,
        height: 20,
        marginLeft: 'auto',
      },
    },
    '&:focus': {
      outline: 0,
      border: 0,
      borderBotom: '2px solid #3f51b5',
    },
  },
});

interface formState {
  title?: string;
  date?: Date;
  number?: string;
  clsType?: { label: string; value: string };
}

interface lightFormState {
  dirDocument?: { label: string; value: Scalars['UUID'] };
}

const Documents: FC<{ readonly?: boolean; id?: string }> = ({ readonly, id }) => {
  const DOWNLOAD_LINK = process.env.REACT_APP_BACKEND_URI?.replace('/graphql', '');

  const defaultNewDocumentState: formState = {
    title: undefined,
    date: undefined,
    number: undefined,
    clsType: undefined,
  };

  const { loading, data, refetch } = useSportingEventQuery({ fetchPolicy: 'no-cache', variables: { id } });

  const { handleSubmit, control, errors, reset, formState } = useForm({
    defaultValues: defaultNewDocumentState,
  });

  const {
    handleSubmit: reglamentDocumentFormHandleSubmit,
    control: reglamentDocumentFormControl,
    errors: reglamentDocumentFormErrors,
    reset: reglamentDocumentFormReset,
    formState: reglamentDocumentFormState,
  } = useForm({
    defaultValues: {
      dirDocument: undefined,
    },
  });

  const {
    handleSubmit: protocolDocumentFormHandleSubmit,
    control: protocolDocumentFormControl,
    errors: protocolDocumentFormErrors,
    reset: protocolDocumentFormReset,
    formState: protocolDocumentFormState,
  } = useForm({
    defaultValues: {
      dirDocument: undefined,
    },
  });

  const {
    handleSubmit: refereeCollegiumDocumentFormHandleSubmit,
    control: refereeCollegiumDocumentFormControl,
    errors: refereeCollegiumDocumentFormErrors,
    reset: refereeCollegiumDocumentFormReset,
    formState: refereeCollegiumDocumentFormState,
  } = useForm({
    defaultValues: {
      dirDocument: undefined,
    },
  });

  const [updateEventData]: UpdateSportingEventMutationHookResult = useUpdateSportingEventMutation({
    update: (proxy, { data }) => {
      proxy.writeQuery({
        query: SportingEventDocument,
        data: {
          ...data?.updateDirSportingEvent,
        },
      });
    },
  });

  const [uploadDocumentFile]: UploadFileMutationHookResult = useUploadFileMutation();

  const [competitionReglaments, competitionProtocols, refereeCollegiumDocuments] = [
    data?.dirSportingEvent?.competitionReglaments,
    data?.dirSportingEvent?.competitionProtocols,
    data?.dirSportingEvent?.refereeCollegiumDocuments,
  ];

  const [isAddingNewReglamentDocument, setIsAddingNewReglamentDocument] = useState(false);
  const [isAddingNewProtocoltDocument, setIsAddingNewProtocolDocument] = useState(false);
  const [isAddingNewRefereeCollegiumDocument, setIsAddingNewRefereeCollegiumDocument] = useState(false);
  const [isAddingNEwDocumentFromSystem, setIsAddingNEwDocumentFromSystem] = useState(false);
  const [isReglamentOrProtocol, setIsReglamentOrProtocol] = useState('');
  const [file, setFile] = useState<Array<File>>([]);

  const { enqueueSnackbar } = useSnackbar();

  const handleSnackBar = (variant: 'default' | 'error' | 'success' | 'warning' | 'info' | undefined, message: string) =>
    enqueueSnackbar(message, { variant });

  const addNewReglamentDocument = async (values: lightFormState) => {
    const { dirDocument } = values;
    try {
      if (isAddingNewReglamentDocument && dirDocument) {
        await updateEventData({
          variables: {
            data: {
              competitionReglaments: {
                connect: [
                  {
                    id: dirDocument?.value,
                  },
                ],
              },
            },
            id,
          },
        });
        handleSnackBar('success', 'документ успешно добавлен');
        reglamentDocumentFormReset();
        setIsAddingNewReglamentDocument(false);
        refetch();
      }
    } catch (error) {
      handleSnackBar('error', getMessage(error.message));
    }
  };
  const addNewProtocolDocument = async (values: lightFormState) => {
    const { dirDocument } = values;
    try {
      if (isAddingNewProtocoltDocument && dirDocument) {
        await updateEventData({
          variables: {
            data: {
              competitionProtocols: {
                connect: [
                  {
                    id: dirDocument?.value,
                  },
                ],
              },
            },
            id,
          },
        });
        handleSnackBar('success', 'документ успешно добавлен');
        protocolDocumentFormReset();
        setIsAddingNewProtocolDocument(false);
        refetch();
      }
    } catch (error) {
      handleSnackBar('error', getMessage(error.message));
    }
  };
  const addNewRefereeCollegiumDocument = async (values: lightFormState) => {
    const { dirDocument } = values;
    try {
      if (isAddingNewRefereeCollegiumDocument && dirDocument) {
        await updateEventData({
          variables: {
            data: {
              refereeCollegiumDocuments: {
                connect: [
                  {
                    id: dirDocument?.value,
                  },
                ],
              },
            },
            id,
          },
        });
        handleSnackBar('success', 'документ успешно добавлен');
        refereeCollegiumDocumentFormReset();
        setIsAddingNewRefereeCollegiumDocument(false);
        refetch();
      }
    } catch (error) {
      handleSnackBar('error', getMessage(error.message));
    }
  };
  const handleAddNewDocumentFromSystem = async (values: formState) => {
    const { title, date, number, clsType } = values;
    try {
      if (isAddingNEwDocumentFromSystem && title && date && file) {
        uploadDocumentFile({
          variables: {
            file: file[0],
          },
        }).then(async (resp: any) => {
          const {
            uploadFile: { id: fileId },
          } = resp.data;
          await updateEventData({
            variables: {
              data: (isReglamentOrProtocol === 'protocol' && {
                competitionProtocols: {
                  create: [
                    {
                      title,
                      date,
                      number,
                      clsType: clsType && { connect: { id: clsType?.value } },
                      file: {
                        connect: { id: fileId },
                      },
                    },
                  ],
                },
              }) ||
                (isReglamentOrProtocol === 'referee' && {
                  refereeCollegiumDocuments: {
                    create: [
                      {
                        title,
                        date,
                        number,
                        clsType: clsType && { connect: { id: clsType?.value } },
                        file: {
                          connect: { id: fileId },
                        },
                      },
                    ],
                  },
                }) || {
                  competitionReglaments: {
                    create: [
                      {
                        title,
                        date,
                        number,
                        clsType: clsType && { connect: { id: clsType?.value } },
                        file: {
                          connect: { id: fileId },
                        },
                      },
                    ],
                  },
                },
              id,
            },
          });
          handleSnackBar('success', 'документ успешно добавлен');
          setIsAddingNEwDocumentFromSystem(false);
          reset(defaultNewDocumentState);
          refetch();
        });
      }
    } catch (error) {
      handleSnackBar('error', getMessage(error.message));
    }
  };

  const deleteProtocolDocument = async (documentId: Scalars['UUID']) => {
    try {
      await updateEventData({
        variables: {
          data: {
            competitionProtocols: {
              disconnect: [
                {
                  id: documentId,
                },
              ],
            },
          },
          id,
        },
      });
      handleSnackBar('success', 'документ успешно удалён');
      refetch();
    } catch (error) {
      handleSnackBar('error', getMessage(error.message));
    }
  };
  const deleteReglamentDocument = async (documentId: Scalars['UUID']) => {
    try {
      await updateEventData({
        variables: {
          data: {
            competitionReglaments: {
              disconnect: [
                {
                  id: documentId,
                },
              ],
            },
          },
          id,
        },
      });
      handleSnackBar('success', 'документ успешно удалён');
      refetch();
    } catch (error) {
      handleSnackBar('error', getMessage(error.message));
    }
  };
  const deleteRefereeCollegiumDocument = async (documentId: Scalars['UUID']) => {
    try {
      await updateEventData({
        variables: {
          data: {
            refereeCollegiumDocuments: {
              disconnect: [
                {
                  id: documentId,
                },
              ],
            },
          },
          id,
        },
      });
      handleSnackBar('success', 'документ успешно удаленн');
      refetch();
    } catch (error) {
      handleSnackBar('error', getMessage(error.message));
    }
  };
  const classes = useStyles();

  if (loading) return <LinearProgress color="primary" variant="query" />;

  const dChanges = detectedChanges(data?.dirSportingEvent).documentsArrayChange;

  return (
    <div>
      <Grid container className={classes.verticalSpaceMarge}>
        <Grid item container alignItems="center">
          <Grid item lg={4} md={6} sm={12}>
            <span className={classes.title}>Протоколы соревнований:&nbsp;</span>
          </Grid>
          {!readonly && (
            <Grid item container spacing={2} alignItems="center" lg={6} md={6} sm={12} xs={12}>
              {isAddingNewProtocoltDocument ? (
                <React.Fragment>
                  <form onSubmit={protocolDocumentFormHandleSubmit(addNewProtocolDocument)} style={{ width: '100%' }}>
                    <Grid item container spacing={2} alignItems="flex-end">
                      <Grid item md={5} xs={12}>
                        <DocumentSelect
                          filter={{
                            id_not_in: competitionProtocols?.map((protocol) => protocol.id),
                          }}
                          label="Документ"
                          control={protocolDocumentFormControl}
                          error={!!protocolDocumentFormErrors['dirDocument']}
                          name="dirDocument"
                          rules={{ required: true }}
                        />
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          style={{ marginRight: '0.5rem' }}
                          startIcon={<SaveIcon />}
                          type="submit"
                          disabled={!protocolDocumentFormState.isDirty}
                        >
                          Сохранить
                        </Button>

                        <Button
                          variant="outlined"
                          color="secondary"
                          style={{ marginRight: '0.5rem' }}
                          size="small"
                          onClick={() => {
                            setIsAddingNewProtocolDocument(false);
                            protocolDocumentFormReset();
                          }}
                        >
                          Отменить
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Grid item>
                    <span style={{ marginRight: '0.5rem' }}>Добавить документ</span>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      color="primary"
                      style={{ marginRight: '0.5rem' }}
                      size="small"
                      type="button"
                      onClick={() => setIsAddingNewProtocolDocument(true)}
                    >
                      Из базы
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      style={{ marginRight: '0.5rem' }}
                      size="small"
                      type="button"
                      onClick={() => {
                        setIsReglamentOrProtocol('protocol');
                        setIsAddingNEwDocumentFromSystem(true);
                      }}
                    >
                      Новый
                    </Button>
                  </Grid>
                </React.Fragment>
              )}
            </Grid>
          )}
        </Grid>
        {competitionProtocols?.map((protocol) => (
          <Grid item container alignItems="center" spacing={2} key={protocol.id}>
            <Grid item>
              <Link href={`${DOWNLOAD_LINK}${protocol.file.path}`} className={classes.link}>
                {protocol.title}
              </Link>
            </Grid>
            <Grid item>
              <span>{protocol.date && moment(protocol.date).format('DD.MM.YYY')}</span>
            </Grid>
            <Grid item>{protocol.number}</Grid>
            <Grid item>{protocol.clsType?.label}</Grid>

            <Grid item>
              {!readonly && (
                <Tooltip title="удалить" placement="top-start">
                  <IconButton
                    className={classes.btn}
                    color="secondary"
                    onClick={() => deleteProtocolDocument(protocol.id)}
                  >
                    <DeleteIcon className={classes.icon} />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
            {dChanges?.includes(protocol?.id) && (
              <Grid item>
                <EditLocationIcon color="secondary" />{' '}
              </Grid>
            )}
          </Grid>
        ))}
      </Grid>
      <Grid container className={classes.verticalSpaceMarge}>
        <Grid item container alignItems="center">
          <Grid item lg={4} md={6} sm={12}>
            <span className={classes.title}>Судейская коллегия:&nbsp;</span>
          </Grid>
          {!readonly && (
            <Grid item container spacing={2} alignItems="center" lg={6} md={6} sm={12} xs={12}>
              {isAddingNewRefereeCollegiumDocument ? (
                <React.Fragment>
                  <form
                    onSubmit={refereeCollegiumDocumentFormHandleSubmit(addNewRefereeCollegiumDocument)}
                    style={{ width: '100%' }}
                  >
                    <Grid item container spacing={2} alignItems="flex-end">
                      <Grid item md={5} xs={12}>
                        <DocumentSelect
                          filter={{
                            id_not_in: refereeCollegiumDocuments?.map((protocol) => protocol.id),
                          }}
                          label="Документ"
                          control={refereeCollegiumDocumentFormControl}
                          error={!!refereeCollegiumDocumentFormErrors['dirDocument']}
                          name="dirDocument"
                          rules={{ required: true }}
                        />
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          style={{ marginRight: '0.5rem' }}
                          startIcon={<SaveIcon />}
                          type="submit"
                          disabled={!refereeCollegiumDocumentFormState.isDirty}
                        >
                          Сохранить
                        </Button>

                        <Button
                          variant="outlined"
                          color="secondary"
                          style={{ marginRight: '0.5rem' }}
                          size="small"
                          onClick={() => {
                            setIsAddingNewRefereeCollegiumDocument(false);
                            refereeCollegiumDocumentFormReset();
                          }}
                        >
                          Отменить
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Grid item>
                    <span style={{ marginRight: '0.5rem' }}>Добавить документ</span>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      color="primary"
                      style={{ marginRight: '0.5rem' }}
                      size="small"
                      type="button"
                      onClick={() => setIsAddingNewRefereeCollegiumDocument(true)}
                    >
                      Из базы
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      style={{ marginRight: '0.5rem' }}
                      size="small"
                      type="button"
                      onClick={() => {
                        setIsReglamentOrProtocol('referee');
                        setIsAddingNEwDocumentFromSystem(true);
                      }}
                    >
                      Новый
                    </Button>
                  </Grid>
                </React.Fragment>
              )}
            </Grid>
          )}
        </Grid>
        {refereeCollegiumDocuments?.map((protocol) => (
          <Grid item container alignItems="center" spacing={2} key={protocol.id}>
            <Grid item>
              <Link href={`${DOWNLOAD_LINK}${protocol.file.path}`} className={classes.link}>
                {protocol.title}
              </Link>
            </Grid>
            <Grid item>
              <span>{protocol.date && moment(protocol.date).format('DD.MM.YYY')}</span>
            </Grid>
            <Grid item>{protocol.number}</Grid>
            <Grid item>{protocol.clsType?.label}</Grid>

            <Grid item>
              {!readonly && (
                <Tooltip title="удалить" placement="top-start">
                  <IconButton
                    className={classes.btn}
                    color="secondary"
                    onClick={() => deleteRefereeCollegiumDocument(protocol.id)}
                  >
                    <DeleteIcon className={classes.icon} />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
            {dChanges?.includes(protocol?.id) && (
              <Grid item>
                <EditLocationIcon color="secondary" />
              </Grid>
            )}
          </Grid>
        ))}
      </Grid>
      <Grid container className={classes.verticalSpaceMarge}>
        <Grid item container alignItems="center" spacing={2}>
          <Grid item lg={4} md={6} sm={12}>
            <span className={classes.title}>Регламент соревнования:&nbsp;</span>
          </Grid>
          {!readonly && (
            <Grid item container spacing={2} alignItems="center" lg={6} md={6} sm={12} xs={12}>
              {isAddingNewReglamentDocument ? (
                <React.Fragment>
                  <form onSubmit={reglamentDocumentFormHandleSubmit(addNewReglamentDocument)} style={{ width: '100%' }}>
                    <Grid item container spacing={2} alignItems="flex-end">
                      <Grid item md={5} xs={12}>
                        <DocumentSelect
                          filter={{
                            id_not_in: competitionReglaments?.map((reglament) => reglament.id),
                          }}
                          label="Документ"
                          control={reglamentDocumentFormControl}
                          error={!!reglamentDocumentFormErrors['dirDocument']}
                          name="dirDocument"
                          rules={{ required: true }}
                        />
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          style={{ marginRight: '0.5rem' }}
                          startIcon={<SaveIcon />}
                          type="submit"
                          disabled={!reglamentDocumentFormState.isDirty}
                        >
                          Сохранить
                        </Button>

                        <Button
                          variant="outlined"
                          color="secondary"
                          style={{ marginRight: '0.5rem' }}
                          size="small"
                          onClick={() => {
                            setIsAddingNewReglamentDocument(false);
                            reglamentDocumentFormReset();
                          }}
                        >
                          Отменить
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Grid item>
                    <span style={{ marginRight: '0.5rem' }}>Добавить документ</span>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      color="primary"
                      style={{ marginRight: '0.5rem' }}
                      size="small"
                      type="button"
                      onClick={() => setIsAddingNewReglamentDocument(true)}
                    >
                      Из базы
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      style={{ marginRight: '0.5rem' }}
                      size="small"
                      type="button"
                      onClick={() => {
                        setIsReglamentOrProtocol('reglament');
                        setIsAddingNEwDocumentFromSystem(true);
                      }}
                      disabled={isAddingNewReglamentDocument}
                    >
                      Новый
                    </Button>
                  </Grid>
                </React.Fragment>
              )}
            </Grid>
          )}
        </Grid>
        {competitionReglaments?.map((reglament) => (
          <Grid item container alignItems="center" spacing={2} key={reglament.id}>
            <Grid item>
              <Link href={`${DOWNLOAD_LINK}${reglament.file.path}`} className={classes.link}>
                {reglament.title}
              </Link>
            </Grid>
            <Grid item>
              <span>{reglament.date && moment(reglament.date).format('DD.MM.YYY')}</span>
            </Grid>
            <Grid item>{reglament.number}</Grid>
            <Grid item>{reglament.clsType?.label}</Grid>

            <Grid item>
              {!readonly && (
                <Tooltip title="удалить" placement="top-start">
                  <IconButton
                    className={classes.btn}
                    color="secondary"
                    onClick={() => deleteReglamentDocument(reglament.id)}
                  >
                    <DeleteIcon className={classes.icon} />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
            {dChanges?.includes(reglament?.id) && (
              <Grid item>
                <EditLocationIcon color="secondary" />{' '}
              </Grid>
            )}
          </Grid>
        ))}
      </Grid>
      <Dialog
        open={isAddingNEwDocumentFromSystem}
        onClose={() => {
          setIsAddingNEwDocumentFromSystem(false);
          setIsReglamentOrProtocol('');
          reset(defaultNewDocumentState);
        }}
        maxWidth="xs"
      >
        <DialogTitle id="add-document-dialog">Новый Документ</DialogTitle>
        <div style={{ padding: '1.5rem' }}>
          <form onSubmit={handleSubmit(handleAddNewDocumentFromSystem)} style={{ width: '100%' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Input
                  label="Название документа"
                  control={control}
                  error={!!errors['title']}
                  name="title"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <DocumentTypes
                  label="Тип документа"
                  error={!!errors['clsType']}
                  control={control}
                  rules={{ required: true }}
                  name="clsType"
                />
              </Grid>

              <Grid item xs={12}>
                <DatePicker
                  fullWidth
                  label="Дата"
                  control={control}
                  error={!!errors['date']}
                  name="date"
                  rules={{ required: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Input label="Номер документа" control={control} error={!!errors['number']} name="number" />
              </Grid>
              <Grid item xs={12}>
                <DropzoneArea
                  showFileNames
                  alertSnackbarProps={{
                    anchorOrigin: { vertical: 'top', horizontal: 'right' },
                  }}
                  dropzoneClass={classes.dropZone}
                  showAlerts={['error']}
                  getFileAddedMessage={(fileName: string) => `Файл ${fileName} успешно загружено`}
                  getDropRejectMessage={(file: File) =>
                    `Файл ${file.name} отклонен. Тип файла не поддерживается. Файл слишком большой. Максимальный размер - 10 мегабайт.`
                  }
                  filesLimit={1}
                  maxFileSize={10485760}
                  clearOnUnmount
                  initialFiles={file}
                  onChange={(fileArray: File[]) => setFile(fileArray)}
                  dropzoneText="Файл"
                />
              </Grid>
              <Grid item container justify="flex-end">
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<SaveIcon />}
                  type="submit"
                  disabled={!(formState.isDirty && file.length > 0)}
                >
                  Добавить
                </Button>
              </Grid>
            </Grid>
          </form>
        </div>
      </Dialog>
    </div>
  );
};

export default Documents;
